import { useAuth0, withAuthenticationRequired } from "@auth0/auth0-react";
import { LoadingSpinner } from "components/LoadingSpinner";
import { PoweredByBadge } from "components/PoweredByBadge";
import { UserRole } from "constants/userRole";
import { useLoading } from "context/LoadingContext";
import { useUserWithAccount } from "context/UserAccountProvider";
import { ConfirmDialog } from "primereact/confirmdialog";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { authSelector, onTokenChange } from "store/slices/auth";
import { userSelector } from "store/slices/user";
import styled from "styled-components";

import { Footer } from "../Footer";
import { Loading } from "../Loading";
import { Navbar } from "../Navbar";
import { Sidebar, SIDEBAR_WIDTH } from "../Sidebar";

const ContentContainer = styled.div<{ $hideSidebar?: boolean }>`
  --content-gutter: 0.825rem;
  --content-gap-top: 24px;
  /* Enough room for the floating "Powered by" badge to sit below the content
     rather than on top of it: the badge's own height plus its offset from the
     viewport edge, and the same gap again above it. */
  --content-gap-bottom: calc(
    var(--powered-by-badge-height) + var(--powered-by-badge-offset) * 2
  );

  width: 100%;
  min-height: 100vh;

  /* The left side still has to clear the fixed sidebar; --content-gutter is the
     gap between it and the content. */
  padding: calc(var(--navbar-height) + var(--content-gap-top))
    var(--content-gutter) var(--content-gap-bottom)
    ${({ $hideSidebar }) =>
      $hideSidebar
        ? "var(--content-gutter)"
        : `calc(${SIDEBAR_WIDTH} + var(--content-gutter))`};

  background: var(--brand-gray-50);
`;

function AppLayoutElement() {
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { token } = useSelector(authSelector);
  const { isLoading } = useLoading();

  const { accountRefetch } = useUserWithAccount();
  const { currentAccount, scope, name, email } = useSelector(userSelector);

  const initialLoad = async () => {
    let token: string;
    try {
      token = await getAccessTokenSilently();
    } catch (err: any) {
      console.log("error", err);
      loginWithRedirect();
      return;
    }

    dispatch(onTokenChange({ token }));

    setTimeout(() => {
      accountRefetch();
    }, 500);
  };

  useEffect(() => {
    initialLoad();
  }, []);

  useEffect(() => {
    if (scope && location.pathname === "/") {
      navigate(`/hospitals/view/${currentAccount}`);
    }
  }, [scope, location, currentAccount]);

  if (token && currentAccount) {
    const isHospitalStaff = scope === UserRole.HOSPITAL_STAFF;
    //
    return (
      <>
        <Navbar />
        {!isHospitalStaff && <Sidebar />}
        <ConfirmDialog />

        <ContentContainer $hideSidebar={isHospitalStaff}>
          {isLoading && <Loading />}

          <Outlet />
        </ContentContainer>

        {isHospitalStaff && <Footer />}

        <PoweredByBadge />
      </>
    );
  }

  return (
    <ContentContainer>
      <LoadingSpinner />
    </ContentContainer>
  );
}

export const AppLayout = withAuthenticationRequired(AppLayoutElement, {});
